import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Fab,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  CardMedia,
  CardActions,
  Pagination,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Public as PublicIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  MoreVert as MoreVertIcon,
  Image as ImageIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';
import { userBlogAPI } from '../services/api';
import { UserBlogPost, BlogMemory, CreateBlogPostData, UpdateBlogPostData } from '../types';
import { format, parseISO } from 'date-fns';

interface UserBlogsProps {
  // Optional props for future use
}

const UserBlogs: React.FC<UserBlogsProps> = () => {
  // State management
  const [posts, setPosts] = useState<UserBlogPost[]>([]);
  const [memories, setMemories] = useState<BlogMemory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 6;
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [thumbnailDialogOpen, setThumbnailDialogOpen] = useState(false);
  
  // Selected items
  const [selectedPost, setSelectedPost] = useState<UserBlogPost | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Form data
  const [formData, setFormData] = useState<CreateBlogPostData>({
    title: '',
    content: '',
    excerpt: '',
    thumbnail_memory_id: undefined,
    tags: [],
    seo_title: '',
    seo_description: ''
  });

  // Load data on component mount and when filters change
  useEffect(() => {
    loadPosts();
  }, [page, statusFilter]);

  useEffect(() => {
    loadMemories();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const response = await userBlogAPI.getUserBlogPosts({
        status: statusFilter === 'all' ? undefined : statusFilter,
        page,
        limit
      });
      
      setPosts(response.data.posts || []);
      setTotalPages(Math.ceil((response.data.total || 0) / limit));
      setError(null);
    } catch (err: any) {
      setError('Failed to load blog posts');
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMemories = async () => {
    try {
      const response = await userBlogAPI.getMemoriesForThumbnail();
      setMemories(response.data.memories || []);
    } catch (err: any) {
      console.error('Error loading memories:', err);
    }
  };

  const handleCreatePost = async () => {
    try {
      const response = await userBlogAPI.createBlogPost(formData);
      await loadPosts();
      setCreateDialogOpen(false);
      resetForm();
      // Show success message
    } catch (err: any) {
      setError('Failed to create blog post');
      console.error('Error creating post:', err);
    }
  };

  const handleUpdatePost = async () => {
    if (!selectedPost) return;
    
    try {
      await userBlogAPI.updateBlogPost(selectedPost.id, formData);
      await loadPosts();
      setEditDialogOpen(false);
      setSelectedPost(null);
      resetForm();
    } catch (err: any) {
      setError('Failed to update blog post');
      console.error('Error updating post:', err);
    }
  };

  const handleDeletePost = async () => {
    if (!selectedPost) return;
    
    try {
      await userBlogAPI.deleteBlogPost(selectedPost.id);
      await loadPosts();
      setDeleteDialogOpen(false);
      setSelectedPost(null);
    } catch (err: any) {
      setError('Failed to delete blog post');
      console.error('Error deleting post:', err);
    }
  };

  const handlePublishPost = async (post: UserBlogPost) => {
    try {
      await userBlogAPI.publishBlogPost(post.id);
      await loadPosts();
    } catch (err: any) {
      setError('Failed to publish blog post');
      console.error('Error publishing post:', err);
    }
  };

  const handleRequestFeatured = async (post: UserBlogPost) => {
    try {
      await userBlogAPI.requestFeatured(post.id);
      await loadPosts();
    } catch (err: any) {
      setError('Failed to request featured status');
      console.error('Error requesting featured:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      thumbnail_memory_id: undefined,
      tags: [],
      seo_title: '',
      seo_description: ''
    });
  };

  const openEditDialog = (post: UserBlogPost) => {
    setSelectedPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      thumbnail_memory_id: post.thumbnail_memory_id,
      tags: post.tags || [],
      seo_title: post.seo_title || '',
      seo_description: post.seo_description || ''
    });
    setEditDialogOpen(true);
    setMenuAnchor(null);
  };

  const openDeleteDialog = (post: UserBlogPost) => {
    setSelectedPost(post);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getFeaturedStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending_review': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const renderPostCard = (post: UserBlogPost) => (
    <Card key={post.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {post.thumbnail_url && (
        <CardMedia
          component="img"
          height={200}
          image={post.thumbnail_url}
          alt={post.title}
          sx={{ objectFit: 'cover' }}
        />
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h3" gutterBottom>
          {post.title}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          {post.excerpt || post.content.substring(0, 150) + '...'}
        </Typography>
        
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Chip 
            label={post.status} 
            size="small" 
            color={getStatusColor(post.status) as any}
          />
          {post.featured_status !== 'not_requested' && (
            <Chip 
              label={post.featured_status.replace('_', ' ')} 
              size="small" 
              color={getFeaturedStatusColor(post.featured_status) as any}
              icon={<StarIcon />}
            />
          )}
        </Stack>
        
        <Typography variant="caption" color="text.secondary">
          Updated: {format(parseISO(post.updated_at), 'MMM dd, yyyy')}
        </Typography>
        
        {/* Show rejection feedback */}
        {post.featured_status === 'rejected' && post.admin_notes && (
          <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Featured Request Rejected:
            </Typography>
            <Typography variant="body2">
              {post.admin_notes}
            </Typography>
          </Alert>
        )}
        
        {post.tags && post.tags.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {post.tags.map((tag, index) => (
              <Chip key={index} label={tag} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
          </Box>
        )}
      </CardContent>
      
      <CardActions>
        <Button size="small" onClick={() => openEditDialog(post)}>
          <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
          Edit
        </Button>
        
        {post.status === 'draft' && (
          <Button size="small" onClick={() => handlePublishPost(post)}>
            <PublicIcon fontSize="small" sx={{ mr: 0.5 }} />
            Publish
          </Button>
        )}
        
        {post.status === 'published' && post.featured_status === 'not_requested' && (
          <Button size="small" onClick={() => handleRequestFeatured(post)}>
            <StarBorderIcon fontSize="small" sx={{ mr: 0.5 }} />
            Request Featured
          </Button>
        )}
        
        {post.status === 'published' && post.featured_status === 'rejected' && (
          <Button size="small" onClick={() => handleRequestFeatured(post)} color="warning">
            <StarBorderIcon fontSize="small" sx={{ mr: 0.5 }} />
            Resubmit for Featured
          </Button>
        )}
        
        <IconButton 
          size="small" 
          onClick={(e) => {
            setSelectedPost(post);
            setMenuAnchor(e.currentTarget);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </CardActions>
    </Card>
  );

  const renderPostList = (post: UserBlogPost) => (
    <Card key={post.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="h3">
              {post.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {post.excerpt || post.content.substring(0, 200) + '...'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip 
                label={post.status} 
                size="small" 
                color={getStatusColor(post.status) as any}
              />
              {post.featured_status !== 'not_requested' && (
                <Chip 
                  label={post.featured_status.replace('_', ' ')} 
                  size="small" 
                  color={getFeaturedStatusColor(post.featured_status) as any}
                  icon={<StarIcon />}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                Updated: {format(parseISO(post.updated_at), 'MMM dd, yyyy')}
              </Typography>
            </Stack>
            
            {/* Show rejection feedback in list view */}
            {post.featured_status === 'rejected' && post.admin_notes && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Featured Request Rejected:
                </Typography>
                <Typography variant="body2">
                  {post.admin_notes}
                </Typography>
              </Alert>
            )}
          </Box>
          
          <Box>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => openEditDialog(post)}>
                <EditIcon fontSize="small" />
              </Button>
              
              {post.status === 'draft' && (
                <Button size="small" onClick={() => handlePublishPost(post)}>
                  <PublicIcon fontSize="small" />
                </Button>
              )}
              
              {post.status === 'published' && post.featured_status === 'not_requested' && (
                <Button size="small" onClick={() => handleRequestFeatured(post)}>
                  <StarBorderIcon fontSize="small" />
                </Button>
              )}
              
              {post.status === 'published' && post.featured_status === 'rejected' && (
                <Button size="small" onClick={() => handleRequestFeatured(post)} color="warning">
                  <StarBorderIcon fontSize="small" />
                </Button>
              )}
              
              <IconButton 
                size="small" 
                onClick={(e) => {
                  setSelectedPost(post);
                  setMenuAnchor(e.currentTarget);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          My Blog Posts
        </Typography>
        
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newViewMode) => newViewMode && setViewMode(newViewMode)}
            size="small"
          >
            <ToggleButton value="grid">
              <GridViewIcon />
            </ToggleButton>
            <ToggleButton value="list">
              <ViewListIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New Post
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Posts</MenuItem>
                <MenuItem value="draft">Drafts</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="featured">Featured</MenuItem>
                <MenuItem value="featured_requests">Featured Requests</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Posts Grid/List */}
      {posts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No blog posts yet
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Create your first blog post to get started!
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create First Post
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 3 }}>
              {posts.map(renderPostCard)}
            </Box>
          ) : (
            <Box>
              {posts.map(renderPostList)}
            </Box>
          )}
          
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => selectedPost && openEditDialog(selectedPost)}>
          <ListItemIcon><EditIcon /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedPost && openDeleteDialog(selectedPost)}>
          <ListItemIcon><DeleteIcon /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={createDialogOpen || editDialogOpen} 
        onClose={() => {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {createDialogOpen ? 'Create New Blog Post' : 'Edit Blog Post'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              fullWidth
              multiline
              rows={2}
              helperText="Optional short description for preview"
            />
            
            <TextField
              label="Content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              fullWidth
              multiline
              rows={8}
              required
            />
            
            <FormControl fullWidth>
              <InputLabel>Thumbnail Memory</InputLabel>
              <Select
                value={formData.thumbnail_memory_id || ''}
                label="Thumbnail Memory"
                onChange={(e) => setFormData({ ...formData, thumbnail_memory_id: e.target.value as number || undefined })}
              >
                <MenuItem value="">No thumbnail</MenuItem>
                {memories.map((memory) => (
                  <MenuItem key={memory.id} value={memory.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar src={memory.thumbnail_url} sx={{ width: 24, height: 24 }} />
                      <Typography variant="body2">{memory.title}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="SEO Title"
              value={formData.seo_title}
              onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
              fullWidth
              helperText="Optional custom title for search engines"
            />
            
            <TextField
              label="SEO Description"
              value={formData.seo_description}
              onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              helperText="Optional description for search engines"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setEditDialogOpen(false);
            resetForm();
          }}>
            Cancel
          </Button>
          <Button 
            onClick={createDialogOpen ? handleCreatePost : handleUpdatePost}
            variant="contained"
          >
            {createDialogOpen ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Blog Post</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedPost?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePost} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserBlogs;